import * as React from "react";
import classnames from "classnames";
import { MenuImage } from "./MenuImage";
import { GroupVisibilityButton } from "./GroupVisibilityButton";
import { SubLegends } from "./SubLegends";
import { ILayerState, ILayer } from "../config";
import { ToggleSwitch } from "../../Elements/ToggleSwitch";
import { GroupVisibility, LayerAction } from "../enums";
import { SubIcons } from "./SubIcons";
import { useMapContext } from "../../Contexts/MapContext";
import { ReactNode } from "react";

export interface IMenuItemProps {
    layer: ILayerState;
    onToggle: (id: string, layerAction: LayerAction) => void;
    onClickLayerMetadata: (
        e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
        layerConfig: ILayer | undefined,
        layer: ILayerState
    ) => void;
    onClickGroupVisibility: (id: string, state: GroupVisibility) => void;
    buttonNode?: ReactNode;
}

export const MenuItem = ({
    layer,
    onToggle,
    onClickLayerMetadata,
    onClickGroupVisibility,
    buttonNode
}: IMenuItemProps): JSX.Element => {
    const { configA } = useMapContext();

    const hasLegend = (layer: ILayerState): boolean => {
        return configA.layers.some((element) => {
            return layer.id.replace(/_/g, "") === element.id.replace(/_/g, "") && element.hideLegend;
        });
    };

    const SubLayers = ({ layers }: { layers: ILayerState[] }) => (
        <ul
            className={classnames(
                "esri-layer-list__list",
                "sublayer",
                "expanded",
                "esri-layer-list__list--independent"
            )}
        >
            {layers.map((layer: ILayerState) => (
                <MenuItem
                    key={layer.id}
                    layer={layer}
                    onToggle={onToggle}
                    onClickLayerMetadata={onClickLayerMetadata}
                    onClickGroupVisibility={(id: string, state: GroupVisibility) => onClickGroupVisibility(id, state)}
                ></MenuItem>
            ))}
        </ul>
    );

    const layerConfig = configA.layers.find((config) => config.id === layer.rootLayerId || config.id === layer.layerId);
    const hasGroupVisibilityButton = layer.expanded && layer.sublayers && layer.sublayers.length && layer.first;
    const hideLegend = hasLegend(layer);
    const hasSublevel = !hideLegend && layer.expanded;
    const tocIcons =
        layerConfig &&
        layerConfig.tocOptions &&
        layerConfig.tocOptions.find(
            (iConfig) =>
                iConfig &&
                (iConfig.layerId.toString() === layer.layerId.toString() ||
                    (layerConfig.layerId && iConfig.layerId.toString() === layerConfig.layerId.toString()))
        )?.icon;
    const tocLabels =
        layerConfig &&
        layerConfig.tocOptions &&
        layerConfig.tocOptions.find(
            (iConfig) =>
                iConfig &&
                (iConfig.layerId.toString() === layer.layerId.toString() ||
                    (layerConfig.layerId && iConfig.layerId.toString() === layerConfig.layerId.toString()))
        )?.label;
    const hasMultipleIcons = tocIcons && Array.isArray(tocIcons);
    const isGrouped = hasMultipleIcons || (!tocIcons && layer.image && layer.image.length > 1);
    const hideToggle =
        layerConfig &&
        layerConfig.tocOptions &&
        layerConfig.tocOptions.find((iConfig) => iConfig.layerId.toString() === layer.layerId && iConfig)?.hideToggle;

    return (
        <>
            {layer.title ? (
                <li
                    key={layer.id}
                    className={classnames(
                        "esri-layer-list__item",
                        "layerWidgetItem",
                        { firstList: layer.first },
                        { flat: !layer.sublayers }
                    )}
                >
                    <div
                        className={classnames(
                            "esri-layer-list__item-container",
                            { grayList: !layer.shown },
                            { firstDivs: layer.first },
                            { expandable: layer.sublayers },
                            { grouped: isGrouped }
                        )}
                    >
                        <MenuImage
                            layer={layer}
                            hideLegend={hideLegend}
                            group={isGrouped}
                            onToggle={onToggle}
                            tocIcon={tocIcons && Array.isArray(tocIcons) ? undefined : (tocIcons as string)}
                        ></MenuImage>
                        <span
                            className="esri-layer-list__item-title customTitle"
                            onClick={() => onToggle(layer.id, LayerAction.Expand)}
                        >
                            {layer.title}
                        </span>
                        {!hideToggle && !layer.hideVisibility ? (
                            <ToggleSwitch
                                id={layer.id + "toggle"}
                                checked={!!layer.visible}
                                onClick={() => onToggle(layer.id, LayerAction.View)}
                            ></ToggleSwitch>
                        ) : null}
                        <span
                            title="Slāņa metadati"
                            onClick={(e) => onClickLayerMetadata(e, layerConfig, layer)}
                            className={classnames("esri-icon-description layer-metadata-btn", {
                                "hidden-element": !(
                                    layerConfig &&
                                    layerConfig.showMetadataLayers &&
                                    layerConfig.showMetadataLayers.find((layerId) => String(layerId) === layer.layerId)
                                )
                            })}
                        ></span>
                        {hasGroupVisibilityButton ? (
                            <GroupVisibilityButton
                                layer={layer}
                                onClick={(id: string, state: GroupVisibility) => onClickGroupVisibility(id, state)}
                            ></GroupVisibilityButton>
                        ) : null}
                        {buttonNode ? buttonNode : null}
                    </div>
                    {hasSublevel && isGrouped && layer.image && !tocIcons ? (
                        <SubLegends shown={layer.shown} images={layer.image}></SubLegends>
                    ) : hasMultipleIcons ? (
                        <SubIcons
                            shown={layer.shown}
                            expanded={layer.expanded}
                            icons={tocIcons}
                            labels={tocLabels as string[]}
                        />
                    ) : null}
                    {hasSublevel && layer.sublayers && layer.sublayers.length && !tocIcons ? (
                        <SubLayers layers={layer.sublayers} />
                    ) : null}
                </li>
            ) : (
                <>
                    {layer.sublayers &&
                        layer.sublayers.length &&
                        layer.sublayers.map((layer: ILayerState) => (
                            <MenuItem
                                key={layer.id}
                                layer={layer}
                                onToggle={onToggle}
                                onClickLayerMetadata={onClickLayerMetadata}
                                onClickGroupVisibility={(id: string, state: GroupVisibility) =>
                                    onClickGroupVisibility(id, state)
                                }
                            ></MenuItem>
                        ))}
                </>
            )}
        </>
    );
};
